import { useSyncExternalStore } from "react";
import { fmtMs } from "@/lib/publishSteps";
import type { Cell } from "@/lib/quietRefresh";

/**
 * Time values that tick WITHOUT re-rendering their parents: each is a tiny
 * component subscribed to one shared clock, so a running step's elapsed time
 * or an "updated 3m ago" label changes its own text node only. The clock
 * stops while the tab is hidden and has no subscribers when nothing on the
 * page shows a live time.
 */
function makeClock(everyMs: number) {
  const subs = new Set<() => void>();
  let timer: ReturnType<typeof setInterval> | undefined;
  let now = Date.now();
  const tick = () => {
    now = Date.now();
    for (const s of [...subs]) s();
  };
  const onVisible = () => {
    if (!document.hidden) tick();
  };
  return {
    subscribe(cb: () => void): () => void {
      subs.add(cb);
      if (subs.size === 1) {
        now = Date.now();
        timer = setInterval(() => {
          if (!document.hidden) tick();
        }, everyMs);
        document.addEventListener("visibilitychange", onVisible);
      }
      return () => {
        subs.delete(cb);
        if (subs.size === 0) {
          if (timer) clearInterval(timer);
          timer = undefined;
          document.removeEventListener("visibilitychange", onVisible);
        }
      };
    },
    get: () => now,
  };
}

const secondClock = makeClock(1000);
const slowClock = makeClock(15_000);

/** "2m 03s" since `startedAt`, ticking every second. */
export function Elapsed({
  startedAt,
  className,
}: {
  startedAt: number;
  className?: string;
}) {
  const now = useSyncExternalStore(
    secondClock.subscribe,
    secondClock.get,
    secondClock.get,
  );
  return (
    <span className={className}>{fmtMs(Math.max(0, now - startedAt))}</span>
  );
}

/** A relative label ("3m ago") for `at` computed by `fmt`, refreshed every
 *  15 s. */
export function Ago({
  at,
  fmt,
  className,
}: {
  at: number | null;
  fmt: (ms: number | null) => string;
  className?: string;
}) {
  useSyncExternalStore(slowClock.subscribe, slowClock.get, slowClock.get);
  return <span className={className}>{fmt(at)}</span>;
}

/** An "updated <ago>" stamp driven by a Cell, so a refresh re-renders this
 *  and nothing else. */
export function UpdatedAgo({
  cell,
  fmt,
}: {
  cell: Cell<number | null>;
  fmt: (ms: number | null) => string;
}) {
  const at = useSyncExternalStore(cell.subscribe, cell.get, cell.get);
  useSyncExternalStore(slowClock.subscribe, slowClock.get, slowClock.get);
  return (
    <span
      className="text-xs text-muted-foreground whitespace-nowrap"
      title={at ? new Date(at).toLocaleString() : undefined}
    >
      {at ? `updated ${fmt(at)}` : ""}
    </span>
  );
}
